// ════════════════════════════════════════════════════════════════════
// STAMP STORAGE  (extracted from App.jsx Jun 19, 2026)
// ════════════════════════════════════════════════════════════════════
// Persistence layer for stamps. Stamps live in localStorage as a flat
// object keyed by stamp ID:
//   { "cherry-blossoms": { state: "someday", createdAt: 1234567890, ... } }
//
// Phase B migration note (Jun 19, 2026): the four functions below were
// previously using raw localStorage + silent catches. They now go
// through safeStorage so quota errors and parse failures surface via
// jgaErrorReport. Return shapes and happy-path behavior are unchanged
// — callers don't need to know they're now talking through a wrapper.
// ════════════════════════════════════════════════════════════════════

import { safeStorage } from "../lib/safeStorage.js";

export const STAMP_STORAGE_KEY = "jga-stamps";


// Read current stamps from localStorage. Returns a flat object keyed
// by stamp ID. Empty object if storage is unreadable, corrupted, or
// hasn't been written to yet.
export function getStamps() {
  const parsed = safeStorage.getJSON(STAMP_STORAGE_KEY, {});
  return (parsed && typeof parsed === "object" && !Array.isArray(parsed)) ? parsed : {};
}


// Persist a stamp's state. State must be "someday" or "visited".
// Preserves existing extended metadata (visitedAt, visitedNote,
// plannedFor) so a state toggle doesn't wipe a user's notes.
// Returns the updated stamps object.
export function setStampState(stampId, state) {
  if (state !== "someday" && state !== "visited") return getStamps();
  const stamps = getStamps();
  const prev = stamps[stampId] || {};
  stamps[stampId] = {
    state,
    createdAt: prev.createdAt || Date.now(),
    updatedAt: Date.now(),
    visitedAt: prev.visitedAt || null,
    visitedNote: prev.visitedNote || null,
    plannedFor: prev.plannedFor || null,
  };
  // When transitioning TO visited and no visitedAt set, stamp it now.
  if (state === "visited" && !stamps[stampId].visitedAt) {
    stamps[stampId].visitedAt = Date.now();
  }
  safeStorage.setJSON(STAMP_STORAGE_KEY, stamps);
  return stamps;
}


// Partial update of a stamp's extended metadata (visitedAt, visitedNote,
// plannedFor). Only updates fields explicitly provided; preserves
// everything else. No-op if the stamp doesn't exist yet.
export function updateStampMeta(stampId, patch) {
  const stamps = getStamps();
  if (!stamps[stampId]) return stamps;
  stamps[stampId] = {
    ...stamps[stampId],
    ...patch,
    updatedAt: Date.now(),
  };
  safeStorage.setJSON(STAMP_STORAGE_KEY, stamps);
  return stamps;
}


// Remove a stamp from the user's collection (returns it to suggested state).
export function clearStamp(stampId) {
  const stamps = getStamps();
  delete stamps[stampId];
  safeStorage.setJSON(STAMP_STORAGE_KEY, stamps);
  return stamps;
}
